import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { useCallback, useEffect, useState }  from 'react';

import { useAddressAutofillCore, useGeocodingCore } from '@mapbox/search-js-react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import CustomInputLabel from './CustomInputLabel';
import CustomHelperText from './CustomHelperText';

const defaultFormData = {
  address_line_1: '',
  address_line_2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
};

const printAddress = (formData) => {
  if (!formData) return null;
  const {address_line_1, address_line_2, address_city, address_state, address_zip, lat, lon} = formData
  let addressStr = '';
  if (address_line_1) addressStr += address_line_1;
  if (address_line_2) addressStr += ` ${address_line_2}`;
  if (address_city) addressStr += `, ${address_city}`;
  if (address_state) addressStr += `, ${address_state}`;
  if (address_zip) addressStr += ` ${address_zip}`;
  // if (lat && lon) addressStr += ` (lat: ${lat}, lon: ${lon})`;
  return addressStr;
};

const handleAddressAutofill = ({
  autofill, 
  setSuggestions, 
  onAddressChange
}) => async e => {
  // console.log('handleAddressAutofill', e?.target?.value);
  const text = e?.target?.value;
  if (!text) return;
  const response = await autofill.suggest(text, {
    // https://docs.mapbox.com/mapbox-search-js/api/react/autofill/
    sessionToken: 'test-123'
  });
  const suggestions = (response?.suggestions || []);
  const addresses = suggestions.map(s => ({
    address_line_1: s?.address_line1,
    address_line_2: s?.address_line2,
    address_city: s?.address_level2,
    address_state: s?.address_level1,
    address_zip: s?.postcode,
    full_address: s?.full_address,
  }));
  setSuggestions(addresses);
  if (addresses?.[0]) onAddressChange(omit(addresses[0], 'full_address'));
};

const handleAddressChange = ({formData, setFormData, onChange}) => address => {
  // when geocoding, drop coordinates from the previous address; the geocode result fills them back in
  const base = omit(formData, ['lat', 'lon']);
  const newVal = {...base, ...address};
  setFormData(newVal);
  if (onChange) onChange(newVal);
};

const handleGeocode = ({
  geocoding,
  setCoordinates,
  setFormData,
  onChange, 
  formData
}) => async ({suggestion}) => {
  const fullAddress = suggestion?.full_address;
  if (!fullAddress) return;
  const response = await geocoding.forward(fullAddress, {limit: 1});
  // a newer geocode request was made while this one was in flight
  const lat = response?.features?.[0]?.geometry?.coordinates?.[1];
  const lon = response?.features?.[0]?.geometry?.coordinates?.[0];
  const newVal = {...formData, ...omit(suggestion, 'full_address'), lat, lon};
  setCoordinates({lat, lon});
  setFormData(newVal);
  if (onChange) onChange(newVal);
};

const handleAutocompleteChange = ({
  suggestions,
  onAddressChange,
  onGeocode,
  debouncedTextChangeHandler,
}) => (e,val,reason) => {
  // console.log('autocomplete.change', e, val, reason, e?.target?.value);
  if (reason === 'selectOption') {
    const selected = suggestions?.[val?.index];
    if (selected) {
      onAddressChange(omit(selected, 'full_address'));
      // geocode explicitly: selecting the top suggestion doesn't re-trigger the effect above
      onGeocode({suggestion: selected});
    }
    return;
  }
  debouncedTextChangeHandler(e);
};

const InnerTextInput = ({
  params, 
  onChange,
  ...props
}) => {
  return (
    <TextField
      onChange={onChange}
      style={{backgroundColor: 'white'}}
      color={props?.color || 'secondary'}
      placeholder={props?.placeholder || 'Start typing a street address...'}
      error={props?.error}
      autoFocus={true}
      {...params}
      size={props?.size || 'small'}
      fullWidth={true}
      minWidth={400}
      InputProps={{
        ...params.InputProps,
        autoFocus: !!props?.autoFocus,
        color: props?.color,
        startAdornment: <InputAdornment position="start">
          <SearchIcon 
            style={{color: config().colors[props.color], fontSize: '1.2rem'}} 
          />
        </InputAdornment>,
        endAdornment: (
          <>
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );
};

const GeocodingForm = ({
  formData,
  suggestions,
  defaultValue,
  handleAutocompleteChange,
  error,
  onAutocompleteChange,
  onInnerTextFieldChange,
  ...props
}) => {
  const autocompleteProps = {
    type: 'text',
    freeSolo: true,
    color: 'secondary',
    options: (suggestions || []).map((s, index) => ({
      label: `${s.address_line_1}, ${s.address_city}, ${s.address_state} ${s.address_zip}`,
      value: index,
      index,
    })),
    filterOptions: (x) => x,
    defaultValue: defaultValue?.address_line_1 || '',
    onChange: onAutocompleteChange,
  };
  return (
    <Stack spacing={2}>
      {
        (props.showLabel || props?.label)
        && <CustomInputLabel required={props?.required || false}>
          {props?.label || 'Address'}
        </CustomInputLabel>
      }
      <Autocomplete
        {...autocompleteProps}
        renderInput={params => (
          <InnerTextInput 
            params={params}
            onChange={onInnerTextFieldChange}
            placeholder={props?.placeholder || 'Start typing a street address...'}
            color={props?.color || 'secondary'}
          />
        )}
      />
      {props?.showAddress && 
        <Typography variant='body'>{printAddress(formData)}</Typography>
      }
      {
        !!error &&
        <Alert severity='error' variant='outlined'>
          {props?.helperText || 'Please enter a session configuration.'}
        </Alert>
      }
      {
        !!props?.helperText && !error &&
        <CustomHelperText>{props?.helperText}</CustomHelperText>
      }
    </Stack>
  );
};

const GeocodingFormContainer = props => {
  const {onChange, defaultValue} = props;
  const [formData, setFormData]  = useState(defaultValue || defaultFormData);
  const [coordinates, setCoordinates] = useState(
    defaultValue && defaultValue?.lat && defaultValue?.lon
    ? {lat: defaultValue.lat, lon: defaultValue.lon}
    : {lat: null, lon: null}
  );
  const [suggestions, setSuggestions] = useState([]);
  // const [selectedSuggestion, setSelectedSuggestion] = useState(null); // TODO
  const autofill = useAddressAutofillCore({accessToken: config().mapBoxPublicApiKey});
  const geocoding = useGeocodingCore({ accessToken: config().mapBoxPublicApiKey });
  const {lat, lon} = coordinates;
  const topSuggestion = suggestions?.[0];
  const onGeocode = handleGeocode({geocoding, setCoordinates, setFormData, formData, onChange});
  useEffect(() => {
    if (topSuggestion) onGeocode({suggestion: topSuggestion, });
  }, [topSuggestion, geocoding]);
  useEffect(() => {
    if (lat && lon) {
      const newVal = {...formData, lat, lon};
      setFormData(newVal);
      if (onChange) onChange(newVal);
    }
  }, [lat, lon]);
  const onAddressChange = handleAddressChange({
    formData,
    setFormData,
  });
  const onTextChange = handleAddressAutofill({
    autofill,
    setSuggestions,
    onAddressChange
  });
  const debouncedTextChangeHandler = useCallback(
    debounce(onTextChange, 500),
    [onTextChange]
  );
  const childProps = {
    // do not pass onChange because it must be handled by the container
    ...omit(props, 'onChange'),
    formData,
    suggestions,
    defaultValue: formData,
    onInnerTextFieldChange: debouncedTextChangeHandler,
    onAutocompleteChange: handleAutocompleteChange({
      suggestions,
      onAddressChange,
      onGeocode,
      debouncedTextChangeHandler,
    }),
  };
  return <GeocodingForm {...childProps} />;
}

GeocodingFormContainer.defaultProps = {
  showLabel: false,
  color: 'primary',
  autoFocus: true,
  showAddress: false,
};

GeocodingFormContainer.propTypes = {
  onChange: PropTypes.func,
  showLabel: PropTypes.bool,
  showAddress: PropTypes.bool,
  autoFocus: PropTypes.bool,
  color: PropTypes.string,
  defaultValue:   PropTypes.shape({
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    address_city: PropTypes.string,
    address_state: PropTypes.string,
    address_zip: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
};

export default GeocodingFormContainer;